<template>
  <form @submit.prevent="signup">
    <b-loading
      :is-full-page="true"
      :active="isLoading"
      :can-cancel="false"
    ></b-loading>

    <h1
      class="is-size-3 has-text-weight-semibold"
      :class="{ 'has-text-white': !isDesktop }"
    >
      Criar conta
    </h1>

    <p :class="{ 'has-text-white': !isDesktop }">
      Criar conta com:
    </p>
    <br />

    <div class="columns">
      <div class="column">
        <b-button type="is-primary" id="email-button" expanded>
          E-mail
        </b-button>
      </div>

      <div class="column has-text-centered">
        <g-signin-button
          :params="googleSignInParams"
          @success="onSignInSuccess"
          @error="onSignInError"
        >
          <img
            src="../../assets/img/g-logo.png"
            alt="Google Logo"
            id="g-logo"
          />
          <span id="google-button-text">Google</span>
        </g-signin-button>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <b-field
          :label="name && isDesktop ? 'Nome' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="name"
            name="name"
            class="is-expanded"
            type="text"
            placeholder="Nome"
            @focus="$v.name.$touch()"
            @blur="testFields"
          ></b-input>
        </b-field>
      </div>

      <div class="column">
        <b-field
          :label="surname && isDesktop ? 'Sobrenome' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="surname"
            name="surname"
            class="is-expanded"
            type="text"
            placeholder="Sobrenome"
            @focus="$v.surname.$touch()"
            @blur="testFields"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <b-field
          :label="email && isDesktop ? 'E-mail pessoal' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="email"
            name="email"
            class="is-expanded"
            type="email"
            placeholder="E-mail pessoal"
            @focus="$v.email.$touch()"
            @blur="testFields"
            :disabled="disableEmail"
          ></b-input>
        </b-field>
      </div>

      <div class="column">
        <b-field
          :label="password && isDesktop ? 'Senha' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="password"
            name="password"
            class="is-expanded"
            type="password"
            placeholder="Senha"
            password-reveal
            @focus="$v.password.$touch()"
            @blur="testFields"
          ></b-input>
        </b-field>
      </div>
    </div>

    <b-field
      :label="state && isDesktop ? 'UF/Cidade' : ''"
      label-position="on-border"
    >
      <b-select
        v-model="state"
        name="state"
        placeholder="UF"
        @input="changeState"
      >
        <option value="">UF</option>
        <option value="Acre">AC</option>
        <option value="Alagoas">AL</option>
        <option value="Amapá">AP</option>
        <option value="Amazonas">AM</option>
        <option value="Bahia">BA</option>
        <option value="Ceará">CE</option>
        <option value="Distrito Federal">DF</option>
        <option value="Espírito Santo">ES</option>
        <option value="Goiás">GO</option>
        <option value="Maranhão">MA</option>
        <option value="Mato Grosso">MT</option>
        <option value="Mato Grosso do Sul">MS</option>
        <option value="Minas Gerais">MG</option>
        <option value="Pará">PA</option>
        <option value="Paraíba">PB</option>
        <option value="Paraná">PR</option>
        <option value="Pernambuco">PE</option>
        <option value="Piauí">PI</option>
        <option value="Rio de Janeiro">RJ</option>
        <option value="Rio Grande do Norte">RN</option>
        <option value="Rio Grande do Sul">RS</option>
        <option value="Rondônia">RO</option>
        <option value="Roraima">RR</option>
        <option value="Santa Catarina">SC</option>
        <option value="São Paulo">SP</option>
        <option value="Sergipe">SE</option>
        <option value="Tocantins">TO</option>
      </b-select>
      <b-autocomplete
        v-model="city"
        name="city"
        expanded
        :data="filteredCitiesArray"
        :open-on-focus="true"
        :keep-first="true"
        placeholder="Cidade"
        @focus="$v.city.$touch()"
        @select="option => (selected = option)"
        @input="testFields"
        :disabled="!isValidState"
      >
        <template slot="empty">
          Sem resultados
        </template>
      </b-autocomplete>
    </b-field>

    <div class="columns">
      <div class="column">
        <b-field
          :label="university && isDesktop ? 'Universidade' : ''"
          label-position="on-border"
        >
          <b-autocomplete
            v-model="university"
            name="university"
            :data="filteredUniversitiesArray"
            :open-on-focus="true"
            :keep-first="true"
            placeholder="Universidade"
            @focus="$v.university.$touch()"
            @select="option => (selected = option)"
            @input="testFields"
            @blur="onchangeUniversity"
            :disabled="!isValidState"
          >
            <template slot="empty">
              Sem resultados
            </template>
          </b-autocomplete>
        </b-field>
      </div>
      <div class="column">
        <b-field
          :label="course && isDesktop ? 'Seu curso' : ''"
          label-position="on-border"
        >
          <b-autocomplete
            v-model="course"
            name="course"
            expanded
            :data="filteredCoursesArray"
            :open-on-focus="true"
            :keep-first="true"
            placeholder="Seu curso"
            @focus="$v.course.$touch()"
            @select="option => (selected = option)"
            @input="testFields"
          >
            <template slot="empty">
              Sem resultados
            </template>
          </b-autocomplete>
        </b-field>
      </div>
    </div>

    <div class="columns" v-show="isAnotherUniversity">
      <div class="column">
        <b-field
          :label="
            anotherUniversity && isDesktop ? 'Qual outra universidade?' : ''
          "
          label-position="on-border"
        >
          <b-input
            v-model="anotherUniversity"
            name="anotherUniversity"
            placeholder="Qual outra universidade?"
            @focus="$v.anotherUniversity.$touch()"
            @blur="testFields"
          ></b-input>
        </b-field>
      </div>
    </div>

    <div class="columns">
      <div class="column">
        <b-field
          :label="areacode && cellphone ? 'DDD + Celular' : ''"
          label-position="on-border"
        >
          <b-select v-model="areacode" id="areacode">
            <option value="">DDD</option>
          </b-select>

          <b-input
            expanded
            v-model="cellphone"
            :value="cellphone"
            type="tel"
            placeholder="00000-0000"
            @input.native="formatCellphoneWithoutAreacode('cellphone')"
          />
        </b-field>
      </div>

      <div class="column is-one-third">
        <b-field
          :label="graduationDate ? 'Formatura' : ''"
          label-position="on-border"
        >
          <b-select v-model="graduationDate" expanded>
            <option value="">Formatura</option>
            <option value="2023.1">2023.1</option>
            <option value="2023.2">2023.2</option>
            <option value="2024.1">2024.1</option>
            <option value="2024.2">2024.2</option>
            <option value="2025.1">2025.1</option>
            <option value="2025.2">2025.2</option>
            <option value="2026.1">2026.1</option>
            <option value="2026.2">2026.2</option>
            <option value="2027.1">2027.1</option>
            <option value="2027.2">2027.2</option>
            <option value="2028.1">2028.1</option>
            <option value="2028.2">2028.2</option>
            <option value="2029+">2029+</option>
            <option value="Já estou formado">Já estou formado</option>
          </b-select>
        </b-field>
      </div>
    </div>

    <p :class="{ 'has-text-white': !isDesktop }">
      Participa de alguma Organização Estudantil? (OE)
    </p>

    <br />

    <div class="columns">
      <div class="column is-narrow">
        <b-field
          :label="organizationTreatmentPronoun && isDesktop ? 'a/o' : ''"
          label-position="on-border"
        >
          <b-select
            v-model="organizationTreatmentPronoun"
            name="organizationTreatmentPronoun"
            expanded
            @input="testFields"
          >
            <option value="">a/o</option>
            <option value="a">a</option>
            <option value="o">o</option>
          </b-select>
        </b-field>
      </div>

      <div class="column">
        <b-field
          :label="organizationName && isDesktop ? 'Nome da OE' : ''"
          label-position="on-border"
        >
          <b-input
            v-model="organizationName"
            name="organizationName"
            class="is-expanded"
            type="text"
            placeholder="Nome da OE"
          ></b-input>
        </b-field>
      </div>

      <div class="column">
        <b-field
          :label="organizationCategory && isDesktop ? 'Categoria da OE' : ''"
          label-position="on-border"
        >
          <b-select
            v-model="organizationCategory"
            name="organizationCategory"
            expanded
            @input="testFields"
          >
            <option value="">Categoria</option>
            <option value="Aerodesign">Aerodesign</option>
            <option value="AIESEC">AIESEC</option>
            <option value="Atlética Universitária">
              Atlética Universitária
            </option>
            <option value="Baja">Baja</option>
            <option value="Barco Solar">Barco Solar</option>
            <option value="Centro Acadêmico">Centro Acadêmico</option>
            <option value="Clube de Consultoria">Clube de Consultoria</option>
            <option value="Clube de Empreendedorismo">
              Clube de Empreendedorismo
            </option>
            <option value="Coletivo">Coletivo</option>
            <option value="Cursos Populares">Cursos Populares</option>
            <option value="Diretório Acadêmico">Diretório Acadêmico</option>
            <option value="Eficiência Energética">Eficiência Energética</option>
            <option value="Empresa Júnior">Empresa Júnior</option>
            <option value="Enactus">Enactus</option>
            <option value="Engenheiros sem Fronteiras">
              Engenheiros sem Fronteiras
            </option>
            <option value="Equipe de Foguetes">Equipe de Foguetes</option>
            <option value="Evento">Evento</option>
            <option value="Feira de Carreiras">Feira de Carreiras</option>
            <option value="Fórmula">Fórmula</option>
            <option value="Grupo de Estudos">Grupo de Estudos</option>
            <option value="Grupo de Extensão">Grupo de Extensão</option>
            <option value="Grupo de Pesquisa">Grupo de Pesquisa</option>
            <option value="IEEE">IEEE</option>
            <option value="Liga Acadêmica">Liga Acadêmica</option>
            <option value="Liga de Mercado Financeiro">
              Liga de Mercado Financeiro
            </option>
            <option value="ONG">ONG</option>
            <option value="PET (Programa de Educação Tutorial)">
              PET (Programa de Educação Tutorial)
            </option>
            <option value="Robótica">Robótica</option>
            <option value="Semana de Curso">Semana de Curso</option>
            <option value="separador" disabled>-------------</option>
            <option value="Outra">Outros Grupos</option>
          </b-select>
        </b-field>
      </div>

      <input
        v-model="honeypot"
        name="jobTitle"
        class="is-expanded"
        type="text"
        placeholder="Cargo"
        hidden
      />
    </div>

    <div class="field">
      <b-checkbox v-model="hasAcceptedTermsAndConditions" type="is-info">
        <span :class="{ 'has-text-white': !isDesktop }">
          Aceito os
        </span>
        <a :href="TERMS_AND_CONDITIONS" target="_blank">
          Termos de Uso e a Política de Privacidade
        </a>
      </b-checkbox>
    </div>

    <button
      class="button is-primary is-medium is-fullwidth has-text-weight-bold"
    >
      Criar conta
    </button>

    <br />
    <p :class="{ 'has-text-white': !isDesktop }">
      Já tenho uma conta.
      <router-link to="/" class="has-text-primary has-text-weight-semibold">
        Entrar
      </router-link>
    </p>
    <br />
    <br />
  </form>
</template>

<script>
import { cidades_por_estado } from "../../assets/js/cidades-por-estado";
import { estados } from "../../assets/js/estados";
import { cursos } from "../../assets/js/cursos";
import { ddds } from "../../assets/js/ddds";
import { universidades_por_estado } from "../../assets/js/universidades-por-estado";
import { required, requiredIf, email } from "vuelidate/lib/validators";
import { commom } from "../../mixins/commom.js";
import { responsive } from "../../mixins/responsive";
import { mask } from "../../mixins/mask";

export default {
  mixins: [commom, responsive, mask],

  data() {
    return {
      isLoading: false,
      TERMS_AND_CONDITIONS: process.env.VUE_APP_TERMS_AND_CONDITIONS,
      PRIVACY_POLICY: process.env.VUE_APP_PRIVACY_POLICY,
      name: "",
      surname: "",
      googlePictureURL: "",
      id_token: "",
      email: "",
      password: "",
      city: "",
      state: "",
      university: "",
      anotherUniversity: "",
      course: "",
      areacode: "",
      cellphone: "",
      graduationDate: "",
      hasAcceptedTermsAndConditions: false,
      organizationTreatmentPronoun: "",
      organizationName: "",
      organizationCategory: "",
      honeypot: "",
      citiesList: {},
      coursesList: [],
      universitiesList: {},
      statesList: [],
      googleSignInParams: {
        client_id:
          "283862371867-7kekqble7ci0ir4dt2q4urbnihq808r4.apps.googleusercontent.com"
      },
      disableEmail: false
    };
  },

  computed: {
    filteredCitiesArray() {
      let filteredCitiesArray = [];
      if (this.isValidState) {
        filteredCitiesArray = this.citiesList[this.state].filter(option => {
          return (
            option
              .toString()
              .toLowerCase()
              .indexOf(this.city.toLowerCase()) >= 0
          );
        });
      }
      return filteredCitiesArray;
    },

    filteredUniversitiesArray() {
      let filteredUniversitiesArray = [];
      if (this.isValidState) {
        filteredUniversitiesArray = this.universitiesList[this.state].filter(
          option => {
            return (
              option
                .toString()
                .toLowerCase()
                .indexOf(this.university.toLowerCase()) >= 0
            );
          }
        );
      }
      return filteredUniversitiesArray;
    },

    filteredCoursesArray() {
      let filteredCoursesArray = [];
      filteredCoursesArray = this.coursesList.filter(option => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.course.toLowerCase()) >= 0
        );
      });
      return filteredCoursesArray;
    },

    isAnotherUniversity() {
      return this.university === "Outra";
    },

    isValidState() {
      return this.statesList.includes(this.state);
    }
  },

  validations: {
    name: {
      required
    },
    surname: {
      required
    },
    email: {
      required,
      email
    },
    password: {
      required
    },
    city: {
      required,
      function(v) {
        if (this.citiesList[this.state]) {
          return this.citiesList[this.state].indexOf(v) > -1;
        } else {
          return false;
        }
      }
    },
    state: {
      required,
      function(v) {
        return this.statesList.indexOf(v) > -1;
      }
    },
    university: {
      required,
      function(v) {
        if (this.universitiesList[this.state]) {
          return this.universitiesList[this.state].indexOf(v) > -1;
        } else {
          return false;
        }
      }
    },
    hasAcceptedTermsAndConditions: {
      required
    },
    anotherUniversity: {
      required: requiredIf("isAnotherUniversity")
    },
    course: {
      required,
      function(v) {
        return this.coursesList.indexOf(v) > -1;
      }
    }
  },

  methods: {
    onSignInSuccess(googleUser) {
      // `googleUser` is the GoogleUser object that represents the just-signed-in user.
      // See https://developers.google.com/identity/sign-in/web/reference#users
      const profile = googleUser.getBasicProfile();
      console.log(profile);
      this.name = profile.getGivenName();
      this.surname = profile.getFamilyName();
      this.email = profile.getEmail();
      this.disableEmail = true;
      this.googlePictureURL = profile.getImageUrl();
      this.id_token = googleUser.getAuthResponse().id_token;
    },

    onSignInError(error) {
      // `error` contains any error occurred.
      console.log('OH NOES', error);
      this.alertError({ message: "COULD_NOT_SIGN_IN_WITH_GOOGLE" });
    },

    changeState() {
      this.$v.state.$touch();
      this.testFields();
    },

    onchangeUniversity() {
      this.$v.university.$touch();
      this.$v.anotherUniversity.$reset();
      document
        .getElementsByName("anotherUniversity")[0]
        .classList.remove("error-background");
    },

    testFields() {
      let inputs = document.querySelectorAll("input,select");
      for (let i = 0; i < inputs.length; ++i) {
        if (this.$v[inputs[i].name]) {
          if (this.$v[inputs[i].name].$error) {
            document
              .getElementsByName(inputs[i].name)[0]
              .classList.add("error-background");
          } else {
            document
              .getElementsByName(inputs[i].name)[0]
              .classList.remove("error-background");
          }
        }
      }
    },

    async signup() {
      // Execute reCAPTCHA with action "login".
      const token = await this.$recaptcha("signup");

      this.isLoading = true;
      this.$store
        .dispatch("signup", {
          student: {
            name: this.name,
            surname: this.surname,
            email: this.email,
            password: this.password,
            city: this.city,
            state: this.state,
            university: this.university,
            anotherUniversity: this.anotherUniversity,
            course: this.course,
            areacode: this.areacode,
            cellphone: this.cellphone,
            graduationDate: this.graduationDate,
            hasAcceptedTermsAndConditions: this.hasAcceptedTermsAndConditions,
            organizationTreatmentPronoun: this.organizationTreatmentPronoun,
            organizationName: this.organizationName,
            organizationCategory: this.organizationCategory,
            honeypot: this.honeypot,
            id_token: this.id_token
          },
          token
        })
        .then(res => {
          if (res.data.result === "ok") {
            this.$buefy.dialog.alert({
              title: "Cadastro realizado!",
              message:
                "Agora só falta validar seu e-mail clicando no link que lhe enviamos.",
              type: "is-success",
              hasIcon: true,
              icon: "check",
              iconPack: "fa",
              onConfirm: () => this.$router.push("/"),
              onCancel: () => this.$router.push("/")
            });
          } else {
            this.alertError(res.data);
          }
        })
        .catch(() => {
          this.alertError({ message: "UNEXPECTED_ERROR" });
        })
        .finally(() => {
          this.$recaptchaInstance.hideBadge();
          this.isLoading = false;
        });
    }
  },
  mounted() {
    this.citiesList = cidades_por_estado;
    this.coursesList = cursos;
    this.universitiesList = universidades_por_estado;
    this.statesList = estados;
    if (this.$route.query.email) this.email = this.$route.query.email;

    let dddsOptions = "";
    const dddslen = ddds.length;
    for (let i = 0; i < dddslen; i++) 
      dddsOptions += `<option value="${ddds[i]}">${ddds[i]}</option>`;
    document.getElementById("areacode").innerHTML += dddsOptions;
  }
};
</script>

<style scoped>
form {
  width: 60%;
  margin: auto;
  margin-top: 2vh;
}
@media (min-width: 1023px) {
  form {
    width: 80%;
    margin-top: 2vh;
  }
}
@media (max-width: 600px) {
  form {
    width: 70%;
  }
}
@media (max-width: 540px) {
  form {
    width: 90%;
  }
}
.error-background {
  background-color: #ff9797 !important;
}
#g-logo {
  width: 20px;
  position: relative;
  top: 3px;
  left: -3px;
}
#email-button,
.g-signin-button {
  border-radius: 18px;
}
.g-signin-button {
  background-color: white;
  width: 100%;
  display: inline-block;
  padding: 4px 8px;
  border-radius: 18px;
  border: 1px solid rgba(0, 0, 0, 0.54);
  color: rgba(0, 0, 0, 0.54);
  cursor: pointer;
}
#google-button-text {
  position: relative;
  right: -3px;
  bottom: 1px;
}
</style>
